import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Autocomplete, TextField, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Button, CircularProgress, Box, Typography,
} from '@mui/material';
import { getKits, getKitsOffGrid } from './api';

const fmtBRL = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const TITULOS = { ongrid: 'On-Grid', offgrid: 'Off-Grid', bombeamento: 'Bombeamento' };

// ── Modal "Importar Cadastro Existente" ──────────────────────────────────
// Compartilhado pelos 3 cadastros de kit (AddKit / AddKitOffGrid / AddKitBombeamento).
// Lista os kits já cadastrados da mesma categoria, com filtros de
// Fornecedor / Módulo / Inversor (+ Bateria só para Off-Grid).
const ModalImportarKit = ({ open, onClose, tipo, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [kits, setKits] = useState([]);
  const [filtroFornecedor, setFiltroFornecedor] = useState(null);
  const [filtroModulo, setFiltroModulo] = useState(null);
  const [filtroInversor, setFiltroInversor] = useState(null);
  const [filtroBateria, setFiltroBateria] = useState(null);

  useEffect(() => {
    if (!open) return;
    setFiltroFornecedor(null);
    setFiltroModulo(null);
    setFiltroInversor(null);
    setFiltroBateria(null);

    const carregar = async () => {
      setLoading(true);
      let lista = [];
      if (tipo === 'offgrid') {
        lista = await getKitsOffGrid({});
      } else if (tipo === 'bombeamento') {
        lista = await getKits({ tipokat: 'bombeamento' });
      } else {
        // On-Grid divide a coleção "kits" com o Bombeamento, mas não usa o
        // campo tipokat (confirmado: só os kits de Bombeamento têm tipokat
        // preenchido — os demais ficam sem o campo). O Off-Grid nem entra
        // aqui, pois vive em coleção própria (kitoffgrids, via getKitsOffGrid).
        // Excluímos 'bombeamento' e, por segurança, também 'offgrid' —
        // caso algum registro tenha ficado perdido nesta coleção por engano.
        const todos = await getKits({});
        lista = (todos || []).filter((k) => k.tipokat !== 'bombeamento' && k.tipokat !== 'offgrid');
      }
      setKits(Array.isArray(lista) ? lista : []);
      setLoading(false);
    };
    carregar();
  }, [open, tipo]);

  const fornecedores = useMemo(
    () => [...new Map(kits.filter((k) => k.fornecedor?._id).map((k) => [k.fornecedor._id, k.fornecedor])).values()],
    [kits]
  );
  const modulos = useMemo(
    () => [...new Map(kits.filter((k) => k.modulo?._id).map((k) => [k.modulo._id, k.modulo])).values()],
    [kits]
  );
  const inversores = useMemo(
    () => [...new Map(kits.filter((k) => k.inversor?._id).map((k) => [k.inversor._id, k.inversor])).values()],
    [kits]
  );
  const baterias = useMemo(
    () => [...new Map(kits.filter((k) => k.bateria?._id).map((k) => [k.bateria._id, k.bateria])).values()],
    [kits]
  );

  const kitsFiltrados = useMemo(() => kits.filter((k) => {
    if (filtroFornecedor && k.fornecedor?._id !== filtroFornecedor._id) return false;
    if (filtroModulo && k.modulo?._id !== filtroModulo._id) return false;
    if (filtroInversor && k.inversor?._id !== filtroInversor._id) return false;
    if (tipo === 'offgrid' && filtroBateria && k.bateria?._id !== filtroBateria._id) return false;
    return true;
  }), [kits, filtroFornecedor, filtroModulo, filtroInversor, filtroBateria, tipo]);

  const colSpanVazio = tipo === 'offgrid' ? 6 : 5;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Importar Cadastro Existente — {TITULOS[tipo] || ''}</DialogTitle>
      <DialogContent>
        {/* Filtros */}
        <Grid container spacing={1.5} mb={2} mt={0.5}>
          <Grid item xs={12} sm={tipo === 'offgrid' ? 3 : 4}>
            <Autocomplete size="small" options={fornecedores}
              getOptionLabel={(o) => o.nome || ''}
              isOptionEqualToValue={(o, v) => o._id === v._id}
              value={filtroFornecedor} onChange={(_, v) => setFiltroFornecedor(v)}
              renderInput={(p) => <TextField {...p} label="Fornecedor" />} />
          </Grid>
          <Grid item xs={12} sm={tipo === 'offgrid' ? 3 : 4}>
            <Autocomplete size="small" options={modulos}
              getOptionLabel={(o) => `${o.marca || ''} ${o.potencia || ''} ${o.modelo || ''}`.trim()}
              isOptionEqualToValue={(o, v) => o._id === v._id}
              value={filtroModulo} onChange={(_, v) => setFiltroModulo(v)}
              renderInput={(p) => <TextField {...p} label="Módulo" />} />
          </Grid>
          <Grid item xs={12} sm={tipo === 'offgrid' ? 3 : 4}>
            <Autocomplete size="small" options={inversores}
              getOptionLabel={(o) => o.modelo || ''}
              isOptionEqualToValue={(o, v) => o._id === v._id}
              value={filtroInversor} onChange={(_, v) => setFiltroInversor(v)}
              renderInput={(p) => <TextField {...p} label="Inversor" />} />
          </Grid>
          {tipo === 'offgrid' && (
            <Grid item xs={12} sm={3}>
              <Autocomplete size="small" options={baterias}
                getOptionLabel={(o) => o.modelo || ''}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                value={filtroBateria} onChange={(_, v) => setFiltroBateria(v)}
                renderInput={(p) => <TextField {...p} label="Bateria" />} />
            </Grid>
          )}
        </Grid>

        {/* Lista de kits */}
        {loading ? (
          <Box display="flex" alignItems="center" gap={1} p={2}>
            <CircularProgress size={18} />
            <Typography variant="body2">Carregando kits...</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Fornecedor</TableCell>
                  <TableCell>Módulo</TableCell>
                  <TableCell>Inversor</TableCell>
                  {tipo === 'offgrid' && <TableCell>Bateria</TableCell>}
                  <TableCell align="right">Valor à Vista</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {kitsFiltrados.map((k) => (
                  <TableRow key={k._id} hover sx={{ cursor: 'pointer' }}
                    onClick={() => onConfirm(k)}>
                    <TableCell>{k.nome || '–'}</TableCell>
                    <TableCell>{k.fornecedor?.nome || '–'}</TableCell>
                    <TableCell>{k.modulo?.modelo || k.modulo?.marca || '–'}</TableCell>
                    <TableCell>{k.inversor?.modelo || '–'}</TableCell>
                    {tipo === 'offgrid' && <TableCell>{k.bateria?.modelo || '–'}</TableCell>}
                    <TableCell align="right">{fmtBRL(k.precovenda)}</TableCell>
                  </TableRow>
                ))}
                {kitsFiltrados.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={colSpanVazio}>
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                        Nenhum kit encontrado para o filtro selecionado.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" color="error" onClick={onClose}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
};

ModalImportarKit.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  tipo: PropTypes.oneOf(['ongrid', 'offgrid', 'bombeamento']),
  onConfirm: PropTypes.func,
};

export default ModalImportarKit;